import { apiClient } from '@/lib/api-client';

export type TicketStatus =
  | 'open'
  | 'awaiting_support'
  | 'awaiting_customer'
  | 'resolved'
  | 'closed';

export type TicketPriority = 'low' | 'normal' | 'high' | 'urgent';

/**
 * A ticket as the API returns it.
 *
 * The staff-only fields are optional rather than a separate type: the customer endpoint simply
 * does not send them, and a component that reads `waiting_seconds` on a customer ticket gets
 * `undefined` rather than a wrong number. Two types would be more precise and would mean every
 * shared component — the status pill, the priority chip, the thread — existed twice.
 */
export interface Ticket {
  id: string;
  reference: string;
  subject: string;
  status: TicketStatus;
  status_label: string;
  priority: TicketPriority;
  opened_by_name: string;
  created_at: string;
  updated_at: string;
  last_message_at: string;
  last_message_from: 'customer' | 'staff';
  message_count: number;
  attachment_count: number;
  has_been_answered: boolean;
  is_closed: boolean;

  /** Staff endpoints only. */
  customer_id?: string;
  assigned_to_user_id?: string | null;
  assigned_to_name?: string | null;
  first_response_at?: string | null;
  resolved_at?: string | null;
  closed_at?: string | null;
  /** Seconds the customer has been waiting. Null when they are not the ones waiting. */
  waiting_seconds?: number | null;
}

export interface TicketMessage {
  id: string;
  author_role: 'customer' | 'staff';
  author_name: string;
  body: string;
  /** Always false on the customer endpoint — that query cannot return an internal note at all. */
  is_internal: boolean;
  created_at: string;
}

export interface TicketAttachment {
  id: string;
  filename: string;
  byte_size: number;
  declared_type: string;
  created_at: string;
}

export interface TicketThread {
  ticket: Ticket;
  messages: TicketMessage[];
  attachments: TicketAttachment[];
}

export interface TicketPage {
  items: Ticket[];
  total: number;
  page: number;
  per_page: number;
}

export interface QueuePage extends TicketPage {
  counts: {
    open: number;
    awaiting_support: number;
    awaiting_customer: number;
    unanswered: number;
    resolved: number;
    closed: number;
  };
}

export const supportKeys = {
  tickets: (page: number) => ['support', 'tickets', page] as const,
  thread: (id: string) => ['support', 'ticket', id] as const,
  queue: (filters: string) => ['support', 'queue', filters] as const,
  staffThread: (id: string) => ['support', 'staff-ticket', id] as const,
};

export const supportApi = {
  list: (page = 1) => apiClient.get<TicketPage>(`/support/tickets?page=${page}`),

  thread: (id: string) => apiClient.get<TicketThread>(`/support/tickets/${id}`),

  open: (subject: string, body: string, priority: TicketPriority) =>
    apiClient.post<Ticket>('/support/tickets', { subject, body, priority }),

  reply: (id: string, body: string) =>
    apiClient.post<{ status: string }>(`/support/tickets/${id}/replies`, { body }),

  close: (id: string) =>
    apiClient.post<{ status: string }>(`/support/tickets/${id}/close`, {}),

  attach: (id: string, file: File) => {
    const form = new FormData();
    form.append('file', file);

    return apiClient.post<{ id: string }>(`/support/tickets/${id}/attachments`, form);
  },

  attachmentPath: (ticketId: string, attachmentId: string) =>
    `/support/tickets/${ticketId}/attachments/${attachmentId}`,
};

export const adminSupportApi = {
  queue: (params: { status?: string[]; q?: string; page?: number }) => {
    const search = new URLSearchParams();

    if (params.status !== undefined && params.status.length > 0) {
      search.set('status', params.status.join(','));
    }

    if (params.q !== undefined && params.q.trim() !== '') {
      search.set('q', params.q.trim());
    }

    search.set('page', String(params.page ?? 1));

    return apiClient.get<QueuePage>(`/admin/support/tickets?${search.toString()}`);
  },

  thread: (id: string) => apiClient.get<TicketThread>(`/admin/support/tickets/${id}`),

  /** `internal: true` writes a note the customer never sees. */
  reply: (id: string, body: string, internal: boolean) =>
    apiClient.post<{ status: string }>(`/admin/support/tickets/${id}/replies`, { body, internal }),

  changeStatus: (id: string, status: TicketStatus) =>
    apiClient.post<{ status: string }>(`/admin/support/tickets/${id}/status`, { status }),

  updateMeta: (
    id: string,
    body: { priority?: TicketPriority; assign_to_me?: boolean; unassign?: boolean },
  ) => apiClient.patch<{ status: string }>(`/admin/support/tickets/${id}`, body),

  attach: (id: string, file: File) => {
    const form = new FormData();
    form.append('file', file);

    return apiClient.post<{ id: string }>(`/admin/support/tickets/${id}/attachments`, form);
  },

  attachmentPath: (ticketId: string, attachmentId: string) =>
    `/admin/support/tickets/${ticketId}/attachments/${attachmentId}`,
};

/** Shared by both views so a status never renders one way for staff and another for customers. */
export function statusTone(status: TicketStatus): 'danger' | 'warning' | 'success' | 'muted' {
  switch (status) {
    case 'open':
    case 'awaiting_support':
      return 'warning';
    case 'resolved':
      return 'success';
    case 'awaiting_customer':
    case 'closed':
      return 'muted';
  }
}

export function priorityTone(priority: TicketPriority): 'danger' | 'warning' | 'default' {
  switch (priority) {
    case 'urgent':
      return 'danger';
    case 'high':
      return 'warning';
    default:
      return 'default';
  }
}
